import { EmptyCell } from '@casops/components/tables/formatters/genericFormatters';
import { OLDEST_TIME } from '@casops/config/time';
import { useLocaleFormatter } from '@casops/shared/hooks/useLocaleFormatter';
import { RegisteredAssetDetailModel } from '@casops/store/apis/registeredAssetsApi';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export interface ReportingItem {
  evt: string;
  dt: DateTime;
  date: string;
  time: string;
  when: string;
  old: boolean;
  error: boolean;
}

export interface ReportingStatus {
  subtext: string;
  good: boolean;
  items?: ReportingItem[];
}

interface UseAssetReportingTableDataProps {
  asset?: RegisteredAssetDetailModel;
}

export const useAssetReportingTableData = ({
  asset,
}: UseAssetReportingTableDataProps): ReportingStatus => {
  const { t } = useTranslation();
  const { showDateOnly, showTimeOnly, showRelative, parseFromIso } = useLocaleFormatter();

  return useMemo(() => {
    if (!asset) {
      return { subtext: '', good: false, items: undefined };
    }

    const sevenDaysAgo = DateTime.now().minus({ days: 7 });
    const minTime = DateTime.fromISO(OLDEST_TIME);

    const generateItem = (desc: string, ts?: string): ReportingItem => {
      if (!ts) {
        return {
          evt: desc,
          dt: minTime,
          date: EmptyCell,
          time: EmptyCell,
          when: EmptyCell,
          old: true,
          error: false,
        };
      }
      const parsedTime = parseFromIso(ts);
      const tooOld = parsedTime < sevenDaysAgo;
      return {
        evt: desc,
        dt: parsedTime,
        date: showDateOnly(parsedTime),
        time: showTimeOnly(parsedTime),
        when: showRelative(parsedTime) ?? EmptyCell,
        old: tooOld,
        error: false,
      };
    };

    const itemGoodReport = generateItem(t('LastGoodReport'), asset.tsRp);
    const itemConnected = generateItem(t('LastConnected'), asset.tsRx);

    const items = [itemGoodReport, itemConnected].sort((a, b) => (a.dt < b.dt ? 1 : -1));
    const statusGood = !itemGoodReport.old && !itemConnected.old;
    const subtext = statusGood ? t('NoIssuesDetected') : t('NotReporting');

    return { subtext, good: statusGood, items };
  }, [asset, t, parseFromIso, showDateOnly, showTimeOnly, showRelative]);
};

export default useAssetReportingTableData;
